import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { API, axios } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { PRINCIPALE, traduci, useTraduzioni } from '../i18n';
import { messaggioErrore } from '../components/accesso/campi';
import Icona from '../components/cliente/Icone';
import Anteprima from '../components/documenti/Anteprima';
import BottoniCarica, { useCodaCarica } from '../components/documenti/BottoniCarica';
import CaricamentoMultiplo, {
  MAX_FILE, chiavePeriodo, erroreCaricamento, lottoInCorso, scelteConAttuale, useLotto,
} from '../components/documenti/CaricamentoMultiplo';
import MenuModifica from '../components/documenti/MenuModifica';
import { adessoRoma, ricevutiValidi } from '../utils/percorso';
import { RELAZIONI, scelteIn } from '../utils/profilo';
import {
  caricaDocumento, consegna, contiPersona, descriviVoce, etichettaPeriodo, iconaVoce, quando, raggruppaPerPersona, ricevutiPerPersona,
  scadenzaDiVoce, scaricaZip, sceltePeriodo, testoEntro, titoloMancano, titoloPersona,
} from '../utils/documenti';
import '../components/cliente/documenti.css';

// Documenti del cliente (/documenti): bozza design/documenti-v2.html, vista cliente. Quello che chiediamo (con il
// perché), quello che è arrivato (verificato da chi, consegnato a chi, quando scade), la cronologia e chi li vede.
// I documenti "solo per lo staff" qui non esistono: il backend non li manda nemmeno.

const RIPROVA_ANALISI_MS = 6000;
const OGNI_MEZZO_MINUTO = 30 * 1000;

// "Oggi" della pagina nell'ora di Roma, che passa al giorno dopo da solo: si guarda l'orologio ogni 30 secondi e
// subito tornando sulla pagina (come il calendario). Cambia (e ridisegna) solo quando cambia il giorno.
const stessoGiorno = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
export const useOggiRoma = () => {
  const [oggi, setOggi] = useState(adessoRoma);
  useEffect(() => {
    const controlla = () => {
      if (document.visibilityState === 'hidden') return;
      const adesso = adessoRoma();
      setOggi((prima) => (stessoGiorno(prima, adesso) ? prima : adesso));
    };
    const t = setInterval(controlla, OGNI_MEZZO_MINUTO);
    document.addEventListener('visibilitychange', controlla);
    return () => { clearInterval(t); document.removeEventListener('visibilitychange', controlla); };
  }, []);
  return oggi;
};

// L'icona di stato della riga: classe "stato-doc" (non "st", che è la pagina dello staff in staff.css: con
// quella regola l'icona diventava un ovale alto)
const Stato = ({ tipo }) => (
  <div className={`stato-doc ${tipo}`} aria-hidden="true" data-testid="stato-doc">
    <Icona nome={tipo === 'ok' ? 'si' : tipo === 'sent' ? 'freccia' : tipo === 'bad' ? 'no' : 'piu'} />
  </div>
);

// Una voce di "Ricevuti" conta come arrivata solo se ha almeno un file non rifiutato: la regola è una sola, in
// utils/percorso.js (la usa anche la card Documenti della home)
export { ricevutiValidi };

// "Maksym, Karolina ed Ernesto": in italiano "ed" davanti a una parola che comincia con la e; nelle altre lingue
// la congiunzione del dizionario (comune.e)
export const elencoNomi = (nomi, lingua = PRINCIPALE) => {
  if (nomi.length <= 1) return nomi.join('');
  const ultimo = nomi[nomi.length - 1];
  const e = lingua === PRINCIPALE ? (/^[eEèéÈÉ]/.test(ultimo) ? 'ed' : 'e') : traduci(lingua, 'comune.e');
  return `${nomi.slice(0, -1).join(', ')} ${e} ${ultimo}`;
};

// I bottoni dei file di una voce di "Ricevuti": il periodo o il giorno; se due file hanno la stessa etichetta
// ("Oggi · 03:30" tre volte) si aggiunge il nome del file com'era sul telefono (o il numero)
export const etichetteFile = (docs, oggi, lingua = PRINCIPALE) => {
  const base = docs.map((d, i) => d.periodo_testo || quando(d.data_upload, oggi, lingua) || `file ${i + 1}`);
  const conNome = base.map((b, i) => (base.filter((x) => x === b).length > 1 && docs[i].nome_originale ? `${b} · ${docs[i].nome_originale}` : b));
  return conNome.map((b, i) => (conNome.filter((x) => x === b).length > 1 ? `${b} (${conNome.slice(0, i + 1).filter((x) => x === b).length})` : b));
};

// L'invio di una busta (un documento solo: più foto o PDF diventano un solo file sul server). La coda delle foto
// — anteprima, raddrizzamento, "una busta sola" — la fanno i bottoni condivisi (BottoniCarica), che chiamano
// `onFile(files, { comePdf })` una volta sola a fine coda: qui `comePdf` arriva già pronto.
const useCaricamento = (clienteId, onFatto) => {
  const { t, l: lingua } = useTraduzioni();
  const [attesa, setAttesa] = useState(false);
  const inVolo = useRef(false); // un invio alla volta (doppio tocco con la rete lenta)
  const invia = async (files, opzioni) => {
    if (files.length > MAX_FILE) {
      toast.error(t('documenti.max_file', { max: MAX_FILE }));
      return false;
    }
    if (inVolo.current) return false;
    inVolo.current = true;
    setAttesa(true);
    try {
      await caricaDocumento(clienteId, files, { ...opzioni, lingua });
      toast.success(t(files.length > 1 ? 'documenti.caricati_un_pdf' : 'documenti.caricato_verifichiamo'));
      onFatto();
      return true;
    } catch (error) {
      // mai error.message ("Network Error"): rete e 5xx → "Il servizio non risponde: riprovate tra poco."
      toast.error(erroreCaricamento(error, t('documenti.non_caricato'), lingua));
      return false;
    } finally {
      inVolo.current = false;
      setAttesa(false);
    }
  };
  return { attesa, invia };
};

// Documenti periodici: di che mese (trimestre, anno) è il file. Prima quelli che mancano, poi gli altri recenti.
const DOMANDA_PERIODO = { ultimi_mesi: 'documenti.periodo.mese', trimestre_chiuso: 'documenti.periodo.trimestre', ultimo_anno: 'documenti.periodo.anno', movimenti_dopo_trimestre: 'documenti.periodo.da_quando' };
// le scelte che manca ancora di caricare le dice il server (con l'etichetta in italiano): si riscrivono nella
// lingua del cliente, poi si aggiungono i periodi di oggi
export const scelteDellaVoce = (voce, oggi, lingua = PRINCIPALE) => scelteConAttuale([
  ...(voce.scelte_periodo || []).map((s) => ({ ...s, etichetta: etichettaPeriodo(s.periodo, s.etichetta, lingua) })),
  ...sceltePeriodo(voce.regola_periodo, oggi, lingua),
]);

// Il periodo già scelto nel menu della riga: il primo che manca (giugno, poi luglio…), "" se non ne manca nessuno
export const primoMancante = (voce) => ((voce.scelte_periodo || [])[0] ? chiavePeriodo(voce.scelte_periodo[0].periodo) : '');

export const ASPETTATE = traduci(PRINCIPALE, 'documenti.aspettate');

// "Da ricaricare: foto sfocata" (rifiutato) o "Da ricaricare: scaduto il 31/08/2026" (scaduto: va ricaricato)
export const daRicaricare = (voce) => (voce.rifiuto?.motivo ? voce.rifiuto.motivo : voce.scaduto?.testo || null);

const VoceRichiesta = ({ voce, clienteId, onFatto, onPiuFile, occupato = false, oggi }) => {
  const { t, l: lingua } = useTraduzioni();
  const { attesa, invia } = useCaricamento(clienteId, onFatto);
  // Periodici: il periodo si dice qui (come nel caricamento multiplo), già scelto sul primo che manca. Quando cambia
  // quello che manca (appena caricato giugno) il menu passa al primo che manca ancora: prima restava su giugno e la
  // busta di luglio partiva come giugno (e prendeva il posto di quella di giugno). Se il file dice un altro mese, sul
  // server vince il file (e lo scrive in cronologia).
  const scelte = voce.regola_periodo ? scelteDellaVoce(voce, oggi, lingua) : [];
  const mancanti = (voce.scelte_periodo || []).map((s) => chiavePeriodo(s.periodo)).join(',');
  const [periodo, setPeriodo] = useState(() => primoMancante(voce));
  useEffect(() => { setPeriodo(primoMancante(voce)); }, [mancanti]); // eslint-disable-line
  const periodoScelto = scelte.find((s) => chiavePeriodo(s.periodo) === periodo)?.periodo;
  // di chi è si dice sempre (anche "titolare"): il documento è di chi dice la riga, e il riconoscimento non lo mette
  // in dubbio. Prima per il titolare non si mandava niente e un nome letto male poteva spostarlo.
  const opzioni = { tipo: voce.tipo, personaId: voce.persona || 'titolare', periodo: periodoScelto };
  // Più file per un periodico (tre buste paga): un documento per file, ognuno col suo mese (caricamento multiplo col
  // tipo). Prima diventavano un solo PDF con un mese solo, e gli altri mesi restavano richiesti. Con un caricamento
  // multiplo già in corso non si parte (prima si ripiegava sull'invio normale: di nuovo un PDF solo con un mese solo)
  // `comePdf` lo dicono i bottoni (una foto raddrizzata: il server ne fa un PDF invece di tenere il JPEG)
  const caricaFile = (files, { comePdf } = {}) => {
    if (!(voce.regola_periodo && files.length > 1)) return invia(files, { ...opzioni, comePdf });
    if (occupato || !onPiuFile) {
      toast.error(t('documenti.aspettate'));
      return false;
    }
    return onPiuFile(files, { tipo: voce.tipo, personaId: opzioni.personaId });
  };
  const entro = testoEntro(voce.entro, oggi, lingua);
  const ricaricare = daRicaricare(voce);
  return (
    <div className="riga-doc" data-testid={`richiesto-${voce.tipo}${voce.persona && voce.persona !== 'titolare' ? `-${voce.persona}` : ''}`}>
      <Stato tipo={ricaricare ? 'bad' : 'wait'} />
      <div className="testo">
        <b>{voce.nome}</b>
        {entro && <span className={`pillola ${entro.classe}`} data-testid="entro">{entro.testo}</span>}
        {voce.dettaglio && <span className="meta">{voce.dettaglio.charAt(0).toUpperCase() + voce.dettaglio.slice(1)} · </span>}
        {voce.nota_file && !voce.dettaglio && <span className="meta">{voce.nota_file} · </span>}
        {voce.a_cosa_serve && <span className="perche">{voce.a_cosa_serve}</span>}
        {voce.nota && <span className="nota-staff">{voce.richiesta_da ? `${voce.richiesta_da}: ` : ''}{voce.nota}</span>}
        {ricaricare && <span className="rifiuto" data-testid="da-ricaricare">{t('documenti.da_ricaricare', { motivo: ricaricare })}</span>}
        {/* il permesso di soggiorno con la ricevuta delle Poste già caricata: è in Questura, non lo si richiede */}
        {voce.in_rinnovo && <span className="nota-staff" data-testid="in-rinnovo">{voce.in_rinnovo}</span>}
      </div>
      <div className="azioni-doc">
        {scelte.length > 0 && (
          <select className="lotto-select periodo-doc"
            aria-label={t('documenti.periodo.riga', { nome: voce.nome, domanda: t(DOMANDA_PERIODO[voce.regola_periodo] || 'documenti.periodo.quale') })}
            value={periodo} onChange={(e) => setPeriodo(e.target.value)} disabled={attesa} data-testid="periodo-riga">
            <option value="">{t(DOMANDA_PERIODO[voce.regola_periodo] || 'documenti.periodo.quale')}</option>
            {scelte.map((s) => <option key={chiavePeriodo(s.periodo)} value={chiavePeriodo(s.periodo)}>{s.etichetta}</option>)}
          </select>
        )}
        <BottoniCarica attesa={attesa} onFile={caricaFile} />
      </div>
    </div>
  );
};

// Moduli da firmare dal telefono (incarico, privacy…): si aprono in /firma/:id
const DaFirmare = ({ voci }) => {
  const { t } = useTraduzioni();
  return voci.map((f) => (
    <div className="riga-doc" key={f.id} data-testid={`da-firmare-${f.id}`}>
      <Stato tipo="wait" />
      <div className="testo">
        <b>{t('documenti.da_firmare_nome', { nome: f.nome })}</b>
        <span className="perche">{t('documenti.firma_spiega')}</span>
      </div>
      <div className="azioni-doc"><Link className="btn sm p" to={`/firma/${f.id}`}>{t('documenti.leggi_e_firma')}</Link></div>
    </div>
  ));
};

// "Di chi è questo documento?": il riconoscimento ha letto un nome che non è di nessuno del nucleo (o non l'ha letto),
// quindi il documento non è di nessuno e non chiude nessuna richiesta. Si sceglie la persona, o se ne aggiunge una
// (diventa un familiare con documenti propri, come dal profilo).
export const DiChiE = ({ voce, persone, clienteId, onFatto }) => {
  const { t, l: lingua } = useTraduzioni();
  const [attesa, setAttesa] = useState(false);
  const [nuova, setNuova] = useState(false);
  const [nome, setNome] = useState('');
  const [cognome, setCognome] = useState('');
  const [relazione, setRelazione] = useState('coniuge');
  const inVolo = useRef(false); // un salvataggio alla volta (doppio tocco con la rete lenta)

  const assegna = async (personaId) => {
    if (!personaId || inVolo.current) return;
    inVolo.current = true;
    setAttesa(true);
    try {
      // una riga senza persona è un documento solo, ma se fossero più file valgono tutti
      await Promise.all((voce.documenti || []).map((d) => axios.put(`${API}/documenti/${d.id}/classifica`, { persona_id: personaId })));
      toast.success(t('documenti.grazie_di_chi'));
      onFatto();
    } catch (error) {
      toast.error(messaggioErrore(error, t('documenti.non_salvato'), lingua));
    } finally {
      inVolo.current = false;
      setAttesa(false);
    }
  };

  const aggiungi = async (e) => {
    e.preventDefault();
    if (!nome.trim() || inVolo.current) return;
    inVolo.current = true;
    setAttesa(true);
    let creata = null;
    try {
      const r = await axios.post(`${API}/clienti/${clienteId}/familiari`,
        { nome: nome.trim(), cognome: cognome.trim(), relazione, documenti: true });
      creata = r.data?.id;
    } catch (error) {
      toast.error(messaggioErrore(error, t('documenti.persona_non_aggiunta'), lingua));
    }
    inVolo.current = false;
    setAttesa(false);
    if (creata) {
      setNuova(false);
      await assegna(creata);
    }
  };

  return (
    <div className="di-chi-e" data-testid={`di-chi-e-${docsId(voce)}`}>
      <b>{t('documenti.di_chi_e')}</b>
      <span className="perche">{t('documenti.di_chi_e_spiega')}</span>
      {!nuova ? (
        <div className="scelte">
          <select className="lotto-select" aria-label={t('documenti.di_chi_e')} value="" disabled={attesa}
            data-testid="scegli-persona" onChange={(e) => assegna(e.target.value)}>
            <option value="">{t('documenti.scegliete_persona')}</option>
            {(persone || []).map((p) => <option key={p.id} value={p.id}>{p.etichetta || p.nome}</option>)}
          </select>
          <button type="button" className="btn sm" onClick={() => setNuova(true)} disabled={attesa}>{t('documenti.aggiungi_persona')}</button>
        </div>
      ) : (
        <form className="scelte" onSubmit={aggiungi}>
          <input aria-label={t('profilo.nome')} placeholder={t('profilo.nome')} value={nome} onChange={(e) => setNome(e.target.value)} maxLength={80} />
          <input aria-label={t('profilo.cognome')} placeholder={t('profilo.cognome')} value={cognome} onChange={(e) => setCognome(e.target.value)} maxLength={80} />
          <select className="lotto-select" aria-label={t('documenti.che_rapporto')} value={relazione} onChange={(e) => setRelazione(e.target.value)}>
            {scelteIn(RELAZIONI, 'profilo.scelte.relazioni', lingua).map(([v, n]) => <option key={v} value={v}>{n}</option>)}
          </select>
          <button type="button" className="btn sm" onClick={() => setNuova(false)} disabled={attesa}>{t('comune.annulla')}</button>
          <button type="submit" className={`btn sm p${attesa ? ' busy' : ''}`} disabled={attesa || !nome.trim()}>{t('comune.aggiungi')}</button>
        </form>
      )}
    </div>
  );
};

const docsId = (voce) => (voce.documenti || [])[0]?.id || voce.tipo;

// Un documento è "loro" se l'hanno caricato loro: quelli che abbiamo messo lì noi (un modulo firmato, una
// scansione) restano nostri e non si correggono da qui. I documenti di prima non dicono chi li ha caricati: allora
// valgono come loro, come già fa "Togli" nell'anteprima.
export const suoDelCliente = (doc) => ((doc || {}).caricato_da || {}).tipo !== 'staff';

const VoceRicevuta = ({ voce, onApri, oggi, persone, nucleo, tipi, clienteId, onFatto }) => {
  const { t, l: lingua } = useTraduzioni();
  const docs = voce.documenti || [];
  const scad = scadenzaDiVoce(voce);
  const consegnato = consegna(voce, oggi, lingua);
  const persona = voce.persona && voce.persona !== 'titolare' ? ` · ${voce.persona.split(' ')[0]}` : '';
  const loro = docs.filter(suoDelCliente);
  const etichette = docs.length > 1 ? etichetteFile(docs, oggi, lingua) : [];
  return (
    <div className="riga-doc">
      <Stato tipo={docs.some((d) => d.stato === 'rifiutato') && !docs.some((d) => d.stato === 'verificato') ? 'bad' : iconaVoce(voce)} />
      <div className="testo">
        <div className="tit">
          <span className="mini" aria-hidden="true">{(docs[0]?.content_type || '').includes('pdf') ? 'PDF' : 'IMG'}</span>
          <p><b>{voce.nome}{persona}</b><span className="meta">{voce.nome_originale ? ` · ${voce.nome_originale}` : ''} · {descriviVoce(voce, oggi, lingua)}</span></p>
        </div>
        <div className="pillole-riga">
          {voce.periodico && (voce.periodico.aggiornata
            ? <span className="pillola ok">{t('documenti.copertura_aggiornata')}</span>
            : voce.periodico.manca && <span className="pillola w">{t('documenti.manca_periodo', { periodo: voce.periodico.manca })}</span>)}
          {scad && scad.scadenza_stato === 'scaduto' && <span className="pillola no">{scad.scadenza_testo}</span>}
          {scad && scad.scadenza_stato === 'presto' && <span className="pillola w">{scad.scadenza_testo}</span>}
          {scad && !scad.scadenza_stato && <span className="meta">{scad.scadenza_testo} </span>}
          {consegnato && <span className="pillola b">{consegnato}</span>}
        </div>
        {docs.length > 1 && (
          <div className="file">
            {etichette.map((etichetta, i) => (
              <button key={docs[i].id} type="button" onClick={() => onApri(docs[i])} aria-label={t('documenti.apri_file', { nome: voce.nome, etichetta })}>{etichetta}</button>
            ))}
          </div>
        )}
      </div>
      <div className="azioni-doc">
        {docs.length === 1 && <button type="button" className="btn sm" onClick={() => onApri(docs[0])}>{t('comune.apri')}</button>}
      </div>
      {/* «Modifica» (che documento è, di chi è, il periodo, una nota) sui documenti che hanno caricato loro: lo stesso
          pannello dello staff, senza «solo per noi». Cosa si può correggere lo decide il server: quello che abbiamo
          già verificato lo sistemiamo noi, la nota si può scrivere sempre. Sta in un punto largo quanto la riga,
          perché a 390 px in una colonnina di bottoni sbordava. */}
      {loro.length > 0 && (
        <div className="modifica-doc">
          {docs.map((d, i) => (suoDelCliente(d) ? (
            <span className="uno" key={d.id}>
              <MenuModifica documento={d} clienteId={clienteId} persone={nucleo} tipi={tipi} onFatto={onFatto}
                etichetta={docs.length > 1 ? etichette[i] : null} />
            </span>
          ) : null))}
        </div>
      )}
      {voce.senza_persona && (
        <div style={{ gridColumn: '1 / -1' }}>
          <DiChiE voce={voce} persone={persone} clienteId={clienteId} onFatto={onFatto} />
        </div>
      )}
    </div>
  );
};

// Più file insieme (fino a 20), senza dire che cosa sono: li riconosciamo noi. Dal telefono "Fotografa" scatta
// più foto di seguito, poi "Invia".
const ZonaCarica = ({ onFile, attesa }) => {
  const { t } = useTraduzioni();
  const [sopra, setSopra] = useState(false);
  // I file trascinati fanno la stessa strada dei bottoni (la coda di BottoniCarica, non una copia): le foto
  // passano dall'anteprima per raddrizzarle, poi parte una busta sola con `comePdf`. Prima arrivavano com'erano.
  const { accetta, vista } = useCodaCarica(onFile);
  return (
    <div className={`zona-carica${sopra ? ' sopra' : ''}`} data-testid="zona-carica"
      onDragOver={(e) => { e.preventDefault(); setSopra(true); }} onDragLeave={() => setSopra(false)}
      onDrop={(e) => {
        e.preventDefault();
        setSopra(false);
        const l = Array.from(e.dataTransfer?.files || []);
        // un caricamento alla volta (come i bottoni, spenti durante un caricamento in corso)
        if (l.length && attesa) toast.error(t('documenti.aspettate'));
        else accetta(l);
      }}>
      <b>{t('documenti.zona_trascina')}</b>
      {t('documenti.zona_fino_a', { max: MAX_FILE })}
      <div className="bt">
        <BottoniCarica attesa={attesa} principale={null} etichette={{ file: t('documenti.scegli_file'), foto: t('documenti.fotografa') }} onFile={onFile} />
      </div>
      {vista}
    </div>
  );
};

const DocumentiCliente = () => {
  const { t, l: lingua } = useTraduzioni();
  const { user } = useAuth();
  const clienteId = user?.id;
  const [dati, setDati] = useState(null);
  const [tipi, setTipi] = useState([]);
  const [errore, setErrore] = useState(null); // la prima lettura non è riuscita: messaggio e "Riprovate"
  const [nonAggiornato, setNonAggiornato] = useState(null); // una rilettura non è riuscita: resta l'elenco di prima
  const [aperto, setAperto] = useState(null);
  const [confermaTogli, setConfermaTogli] = useState(false);
  const [togliendo, setTogliendo] = useState(false);
  const [zipInCorso, setZipInCorso] = useState(false);
  const inTogli = useRef(false); // "Sì, togli" due volte con la rete lenta: una sola richiesta
  const inZip = useRef(false); // doppio clic su "Scarica tutto": uno zip solo
  const riquadroLotto = useRef(null);
  const bottoneNo = useRef(null);
  const bottoneTogli = useRef(null);
  const haDati = useRef(false);
  const [erroreTogli, setErroreTogli] = useState(null); // "Togli" non riuscito per la rete: detto nell'anteprima, non in un avviso sopra
  // l'ora di Roma (come le date del server): con il telefono a New York la cronologia diceva l'ora del telefono.
  // Un orologio che avanza (come in CalendarioCliente): lasciata aperta oltre la mezzanotte la pagina restava a ieri
  // ("Oggi · 18:00" per una cosa di ieri, "entro domani" il giorno della scadenza)
  const oggi = useOggiRoma();

  // La pratica è quella che serve; il catalogo dei tipi (i menu del caricamento multiplo) no: se non arriva si usa
  // quello incluso nel sito. Prima un 500 sul catalogo bloccava tutta la pagina; e un'azione riuscita seguita da una
  // rilettura a 503 faceva sparire l'elenco (al suo posto solo l'errore, senza "Riprovate").
  const carica = useCallback(async () => {
    const [pratica, catalogo] = await Promise.allSettled([
      axios.get(`${API}/clienti/${clienteId}/documenti/pratica`),
      axios.get(`${API}/documenti/catalogo`),
    ]);
    if (catalogo.status === 'fulfilled' && Array.isArray(catalogo.value.data?.tipi)) setTipi(catalogo.value.data.tipi);
    if (pratica.status === 'fulfilled') {
      haDati.current = true;
      setDati(pratica.value.data);
      setErrore(null);
      setNonAggiornato(null);
      return;
    }
    const messaggio = messaggioErrore(pratica.reason, t('documenti.non_letti'), lingua);
    if (haDati.current) setNonAggiornato(messaggio);
    else setErrore(messaggio);
  }, [clienteId, t, lingua]);
  const riprova = () => { setErrore(null); carica(); };

  useEffect(() => { if (clienteId) carica(); }, [clienteId, carica]);
  // Nuovo giorno: si rilegge, perché le scadenze dei documenti ("scaduto il…", "scade tra…") le calcola il server
  const oggiLetto = useRef(oggi);
  useEffect(() => {
    if (oggiLetto.current === oggi) return;
    oggiLetto.current = oggi;
    if (clienteId) carica();
  }, [oggi, clienteId, carica]);
  useEffect(() => {
    const precedente = document.title;
    document.title = `MK Client — ${t('documenti.titolo')}`; // prima restava "MK Client"
    return () => { document.title = precedente; };
  }, [t]);
  const { lotto, avvia, leggi, chiudi } = useLotto(clienteId, carica);
  // Un caricamento multiplo partito da una riga in fondo alla pagina (tre buste paga): il riquadro è in cima, si porta in vista
  useEffect(() => {
    if (lotto?.seq) riquadroLotto.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  }, [lotto?.seq]);
  // "Togli" → la conferma prende il fuoco ("No"); "No" lo rimette su "Togli" (prima il fuoco saltava su "Chiudi")
  useEffect(() => {
    if (confermaTogli) bottoneNo.current?.focus();
  }, [confermaTogli]);
  const chiudiAnteprima = useCallback(() => { if (!inTogli.current) { setAperto(null); setConfermaTogli(false); setErroreTogli(null); } }, []);
  // Il documento aperto non c'è più (tolto da un'altra scheda, cambiato dallo staff): si chiude e si rilegge
  const nonDisponibile = useCallback((messaggio) => {
    setAperto(null);
    setConfermaTogli(false);
    setErroreTogli(null);
    toast.error(t('documenti.elenco_aggiornato', { messaggio: String(messaggio).replace(/\.$/, '') }));
    carica();
  }, [carica, t]);
  const nonTogliere = () => {
    setConfermaTogli(false);
    setTimeout(() => bottoneTogli.current?.focus(), 0);
  };
  const scaricaTutto = async () => {
    if (inZip.current) return;
    inZip.current = true;
    setZipInCorso(true);
    try {
      // il nome lo dà il server ("documenti-oleksandr-marchenko.zip"); "documenti.zip" solo se non lo dice
      await scaricaZip(`${API}/clienti/${clienteId}/documenti/zip`, 'documenti.zip');
    } catch (error) {
      toast.error(messaggioErrore(error, t('documenti.zip_non_riuscito'), lingua));
    } finally {
      inZip.current = false;
      setZipInCorso(false);
    }
  };

  // Un documento appena caricato si legge in sottofondo (tipo, periodo, scadenza): si riguarda tra poco
  const inAnalisi = useMemo(() => (dati?.ricevuti || []).some((g) => g.voci.some((v) => v.documenti.some((d) => d.in_analisi))), [dati]);
  useEffect(() => {
    if (!inAnalisi) return undefined;
    const t = setTimeout(carica, RIPROVA_ANALISI_MS);
    return () => clearTimeout(t);
  }, [inAnalisi, carica]);

  if (errore) {
    return (
      <main className="mk-home pagina-documenti wrap" data-testid="documenti-errore">
        <p className="err" role="alert">{errore}</p>
        <button type="button" className="btn sm" onClick={riprova}>{t('comune.riprova')}</button>
      </main>
    );
  }
  if (!dati) return <main className="mk-home pagina-documenti wrap"><div className="attesa"><span>{t('documenti.carico')}</span></div></main>;

  const ricevuti = dati.ricevuti.reduce((n, g) => n + g.voci.length, 0);
  const persone = dati.persone || [];
  // i documenti per persona: i conteggi li fa il server (ricevuti, mancanti, richieste aperte per ciascuno)
  const perPersona = raggruppaPerPersona(dati.per_persona);
  const gruppiPersona = perPersona ? ricevutiPerPersona(dati.ricevuti, dati.per_persona) : [];
  // nel titolo "il resto c'è" solo se qualcosa di valido c'è davvero (i rifiutati non contano); le firme da fare
  // contano tra quello che manca (prima "Ci sono tutti" con l'incarico ancora da firmare)
  const titolo = titoloMancano(dati.mancano + (dati.da_firmare || []).length, ricevutiValidi(dati.ricevuti), lingua);
  // La zona libera e «Carica documenti» in cima: sempre il caricamento multiplo (più file insieme, li riconosciamo
  // noi), anche per una foto sola. Le foto raddrizzate arrivano con come_pdf e il server ne fa un PDF anche nel
  // lotto: prima una foto sola prendeva la scorciatoia del caricamento singolo e il riquadro «Ecco cosa abbiamo
  // riconosciuto» non compariva.
  const caricaLibero = (files) => avvia(files);
  const togli = async (doc) => {
    if (inTogli.current) return;
    inTogli.current = true;
    setTogliendo(true);
    setErroreTogli(null);
    let rifiutato = null;
    try {
      await axios.delete(`${API}/documenti/${doc.id}`);
      setAperto(null);
      setConfermaTogli(false);
      toast.success(t('documenti.tolto'));
      carica();
    } catch (error) {
      // Rifiutato dal server (già tolto da un'altra scheda: 404; verificato dallo staff nel frattempo: 403): la pagina
      // era vecchia, si chiude l'anteprima e si rilegge. Prima restava tutto com'era, con solo un avviso.
      const codice = error?.response?.status;
      if (codice >= 400 && codice < 500 && codice !== 401) {
        rifiutato = codice === 404 ? t('documenti.gia_tolto') : messaggioErrore(error, t('documenti.non_si_puo_togliere'), lingua);
      } else {
        setErroreTogli(messaggioErrore(error, t('documenti.non_tolto'), lingua));
      }
    } finally {
      inTogli.current = false;
      setTogliendo(false);
    }
    if (rifiutato) nonDisponibile(rifiutato);
  };
  return (
    <main className="mk-home pagina-documenti wrap" data-testid="documenti-cliente">
      {nonAggiornato && (
        <p className="avviso-doc" role="status" data-testid="non-aggiornato">
          <span>{t('documenti.non_aggiornato', { motivo: nonAggiornato.replace(/\.$/, '') })}</span>
          <button type="button" className="btn sm" onClick={carica}>{t('comune.riprova')}</button>
        </p>
      )}
      <div className="testa-doc r">
        <div>
          <div className="k">{t('documenti.occhiello')}</div>
          <h1>{titolo.prima}<em>{titolo.em}</em>{titolo.dopo}</h1>
          <p>{t('documenti.sottotitolo')}</p>
        </div>
        {/* «Carica documenti»: la stessa strada degli altri due punti (la coda di BottoniCarica), così anche da qui
            le foto passano dall'anteprima per raddrizzarle e arrivano come PDF. Prima partivano com'erano. */}
        <span className="carica-testa" data-testid="carica-testa">
          <BottoniCarica attesa={lottoInCorso(lotto)} onFile={caricaLibero}
            etichette={{ file: <><Icona nome="piu" />{t('documenti.carica_documenti')}</>, foto: t('documenti.fotografa') }} />
        </span>
      </div>

      <div ref={riquadroLotto} className="posto-lotto">
        {lotto && (
          <CaricamentoMultiplo lotto={lotto} variante="cliente" tipi={tipi} persone={persone} onLeggi={leggi} onChiudi={chiudi}
            onConfermato={() => { carica(); toast.success(t('documenti.fatto_verifichiamo')); }} />
        )}
      </div>

      <div className="colonne">
        <div className="pila">
          <section className="card hero r" data-testid="vi-chiediamo">
            <div className="ttl"><div className="i w"><Icona nome="orologio" /></div><h2>{t('documenti.vi_chiediamo')}</h2></div>
            <DaFirmare voci={dati.da_firmare || []} />
            {dati.richiesti.length === 0 && !(dati.da_firmare || []).length
              ? <p className="testo-muto">{t('documenti.niente_richieste')}</p>
              : dati.richiesti.map((v) => (
                <VoceRichiesta key={`${v.tipo}-${v.persona || 'titolare'}`} voce={v} clienteId={clienteId} onFatto={carica} oggi={oggi}
                  onPiuFile={avvia} occupato={lottoInCorso(lotto)} />
              ))}
            <ZonaCarica onFile={caricaLibero} attesa={lottoInCorso(lotto)} />
          </section>

          <section className="card r" data-testid="ricevuti">
            <div className="ttl"><div className="i g"><Icona nome="si" /></div><h2>{t('documenti.ricevuti_titolo')}</h2>
              {ricevuti > 0 && (
                <a href="#zip" aria-disabled={zipInCorso} aria-busy={zipInCorso} onClick={(e) => { e.preventDefault(); scaricaTutto(); }}>
                  {t(zipInCorso ? 'documenti.preparo_zip' : 'documenti.scarica_tutto')}
                </a>
              )}
            </div>
            {dati.ricevuti.length === 0 && !perPersona && <p className="testo-muto">{t('documenti.nessun_ricevuto')}</p>}
            {/* Con più persone nel nucleo i documenti si raggruppano per persona (con i conteggi); con il solo
                titolare restano i gruppi per categoria, come prima. */}
            {perPersona ? gruppiPersona.map((g) => (
              <React.Fragment key={g.persona.id || 'senza-persona'}>
                <div className="gruppo persona" data-testid={`persona-${g.persona.id || 'senza'}`}>
                  {titoloPersona(g.persona, lingua)}<span>{contiPersona(g.persona, lingua)}</span>
                </div>
                {g.voci.length === 0 && <p className="testo-muto">{t('documenti.niente_per', { nome: g.persona.nome?.split(' ')[0] || t('documenti.questa_persona') })}</p>}
                {g.voci.map((v) => (
                  <VoceRicevuta key={`${v.tipo}-${v.persona_id}-${v.documenti[0]?.id}`} voce={v} oggi={oggi} onApri={setAperto}
                    persone={dati.persone_possibili || []} nucleo={persone} tipi={tipi} clienteId={clienteId} onFatto={carica} />
                ))}
              </React.Fragment>
            )) : dati.ricevuti.map((g) => (
              <React.Fragment key={g.categoria}>
                <div className="gruppo">{g.nome}</div>
                {g.voci.map((v) => <VoceRicevuta key={`${v.tipo}-${v.persona}-${v.documenti[0]?.id}`} voce={v} oggi={oggi} onApri={setAperto}
                  persone={dati.persone_possibili || []} nucleo={persone} tipi={tipi} clienteId={clienteId} onFatto={carica} />)}
              </React.Fragment>
            ))}
          </section>
        </div>

        <div className="pila">
          <section className="card r">
            <div className="ttl"><div className="i"><Icona nome="orologio" /></div><h2>{t('documenti.cronologia')}</h2></div>
            {dati.cronologia.length === 0 ? <p className="testo-muto">{t('documenti.cronologia_vuota')}</p> : (
              <div className="linea-tempo">
                {dati.cronologia.slice(0, 8).map((e, i) => <div key={`${e.data}-${i}`}><b>{e.testo}</b><span>{quando(e.data, oggi, lingua)}</span></div>)}
              </div>
            )}
          </section>
          <section className="card r">
            <div className="ttl"><div className="i b"><Icona nome="casa" /></div><h2>{t('documenti.chi_vede')}</h2></div>
            <p className="testo-muto">
              {t('documenti.chi_vede_testo', {
                chi: elencoNomi([...dati.chi_vede, dati.chi_vede.length ? null : t('documenti.vostro_consulente')].filter(Boolean), lingua),
              })}
            </p>
          </section>
        </div>
      </div>

      {aperto && (
        <Anteprima doc={aperto} onChiudi={chiudiAnteprima} onNonDisponibile={nonDisponibile}
          azioni={aperto.stato !== 'verificato' && aperto.caricato_da?.tipo === 'cliente'
            ? (confermaTogli ? (
              <span className="conferma-togli" role="group" aria-label={t('comune.conferma')}>
                <small role={erroreTogli ? 'alert' : undefined} data-testid="errore-togli">{erroreTogli || t('documenti.togliere_file')}</small>
                <button type="button" ref={bottoneNo} onClick={nonTogliere} disabled={togliendo}>{t('comune.no')}</button>
                <button type="button" className={`no${togliendo ? ' busy' : ''}`} onClick={() => togli(aperto)} disabled={togliendo} aria-busy={togliendo}>
                  {t(togliendo ? 'documenti.tolgo' : 'documenti.si_togli')}
                </button>
              </span>
            ) : <button type="button" ref={bottoneTogli} className="no" onClick={() => setConfermaTogli(true)}>{t('documenti.togli_bottone')}</button>) : null} />
      )}
    </main>
  );
};

export default DocumentiCliente;
